import cn from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import { USER_ROLES } from '@features/auth/utils/constants.ts';
import { IBAN_IT, IBAN_ES, IBAN_DE, PAYEE_DE, BANK_DE } from '@features/users/adminPanel/consts/common';
import { Icon } from '@shared/components/Icon/Icon.tsx';
import { ICON_VARIANTS } from '@shared/components/Icon/IconMapper.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { useCopyToClipboard } from '@shared/hooks/useCopyToClipboard.tsx';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';
import { UserProfileResponse } from '@shared/services/userProfile/service.types.ts';
import { UserCountryCode, UserCountryCodes } from '@shared/types/countryCodes.ts';

import styles from './IbanInfo.module.scss';

interface IbanInfoProps {
  data: UserProfileResponse | null;
}

function determineIBANbyCountryCode(countryCode: UserCountryCode | null) {
  switch (countryCode) {
    case UserCountryCodes.IT: {
      return IBAN_IT;
    }
    case UserCountryCodes.ES: {
      return IBAN_ES;
    }
    case UserCountryCodes.DE: {
      return IBAN_DE;
    }
    default: {
      return IBAN_IT;
    }
  }
}

export function IbanInfo({ data }: IbanInfoProps) {
  const { handleCopyClick } = useCopyToClipboard();
  const { countryCode, isGermanUser } = useUserDetails();
  const { t } = useTranslation(i18nNameSpace.USERS);

  const IBAN = determineIBANbyCountryCode(countryCode);

  if (!data) {
    return null;
  }

  const isExternalAgency = data.role === USER_ROLES.EXTERNAL_AGENCY;

  return (
    <div
      className={cn([
        styles.wrapper,
        {
          [styles.wrapperColumn]: isExternalAgency,
        },
      ])}
    >
      <div>
        <Icon variant={ICON_VARIANTS.wallet} iconAltText='Wallet' />
      </div>
      <div className={styles.infoSection}>
        <p>
          <Trans
            ns={i18nNameSpace.USERS}
            i18nKey='page.adminPanel.ibanInfo.general'
            components={[<strong key={0} className={styles.boldText} />, <span key={1} className={styles.highlight} />]}
          />
        </p>
        <p className={styles.iban}>
          <strong>{IBAN}</strong>
          <i className={`pi pi-copy ${styles.copyIcon}`} onClick={() => handleCopyClick(IBAN)}></i>
        </p>
        {isGermanUser && (
          <>
            <p>
              <strong>{t('page.adminPanel.ibanInfo.payeeLabel')}: </strong>
              {PAYEE_DE}
            </p>
            <p>
              <strong>{t('page.adminPanel.ibanInfo.bankLabel')}: </strong>
              {BANK_DE}
            </p>
          </>
        )}
        {isExternalAgency && (
          <p>
            <Trans ns={i18nNameSpace.USERS} i18nKey='page.adminPanel.ibanInfo.agencyInfo' />
          </p>
        )}
      </div>
    </div>
  );
}
